"use client";
import { faHeart } from "@fortawesome/pro-light-svg-icons/faHeart";
import { faHeart as faHeartSolid } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { analytics } from "@yardzen/next-segment";
import { ClickedEvent } from "@yardzen/segmentv2";
import * as React from "react";
import { cn } from "../lib/utils";
import { SegmentClickTypes } from "../types";
import { Card } from "./card";

type BaseStyleCardProps = {
  image: React.ReactNode;
  className?: string;
  value?: string;
  text?: string;
  responseType: "single-choice" | "multiple-choice";
  tracking?: Omit<ClickedEvent, "click_type"> & {
    click_type?: SegmentClickTypes;
  };
  waitForTracking?: boolean;
  pressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
};

export const StyleCardToggle = React.forwardRef<
  HTMLButtonElement,
  BaseStyleCardProps
>(
  (
    {
      image,
      className,
      value,
      text,
      responseType,
      tracking,
      waitForTracking,
      pressed,
      onPressedChange,
      ...props
    },
    ref,
  ) => {
    const handleStateChange = async (newPressed: boolean) => {
      if (tracking) {
        const promise = analytics.trackClicked({
          ...tracking,
          click_type: tracking.click_type || SegmentClickTypes.BUTTON,
          ...(newPressed ? { liked: true } : { liked: false }),
        });
        if (waitForTracking) {
          await promise;
        }
      }
      onPressedChange?.(newPressed);
    };

    const cardContent = (
      <Card
        role="presentation"
        className="font-body relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg"
      >
        <div className="relative w-full pb-[103.5%]">
          <div className="absolute inset-0">
            {React.cloneElement(image as React.ReactElement<any>, {
              className: "h-full w-full object-cover",
            })}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-black/30" />
            <div className="absolute bottom-3 right-3">
              <div className="group-data-[state=checked]:bg-brand-tertiary-50 group-data-[state=checked]:text-brand-primary relative h-8 w-8 rounded-full bg-white/80 p-1.5 shadow-sm backdrop-blur-sm transition-colors">
                <FontAwesomeIcon
                  icon={faHeart}
                  className="h-5 w-5 group-data-[state=checked]:hidden"
                />
                <FontAwesomeIcon
                  icon={faHeartSolid}
                  className="hidden h-5 w-5 group-data-[state=checked]:block"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    );

    const sharedClassName = cn("group focus-visible:outline-none", className);

    return responseType === "multiple-choice" ? (
      <Checkbox.Root
        ref={ref}
        {...props}
        value={value}
        checked={pressed}
        onCheckedChange={handleStateChange}
        className={sharedClassName}
      >
        {cardContent}
      </Checkbox.Root>
    ) : (
      <RadioGroup.Item
        ref={ref}
        {...props}
        value={value ?? ""}
        className={sharedClassName}
      >
        {cardContent}
      </RadioGroup.Item>
    );
  },
);

StyleCardToggle.displayName = "StyleCardToggle";
